/**
 * RedlineInserter - STAGE 3: PRESENTATION
 * 
 * Handles insertion of content with track changes into Word documents.
 * 
 * Key feature: insertAsTrackedChangeWithSearch now supports OOXML insertion
 * - When editedOoxml is provided, tries insertOoxml first (word-level track changes)
 * - Falls back to insertText if OOXML insertion fails
 * - Uses OoxmlPackageWrapper to wrap paragraph OOXML in package format
 * - Preserves w:ins and w:del elements so Word displays native track changes
 */

import { TrackChangesManager } from './TrackChangesManager';
import { DocumentReader } from '../api/DocumentReader';
import { TrackChangesParser } from '../utils/TrackChangesParser';
import { OoxmlPackageWrapper } from '../utils/OoxmlPackageWrapper';
import { computeWordDiff } from '../utils/TextDiff';
import { log, logError, logWarn } from '../../utils/logging';

/* global Word */

/** Word.js Range.search rejects strings longer than this (RichApi: SearchStringInvalidOrTooLong). */
export const WORD_JS_MAX_SEARCH_STRING_LENGTH = 255;

/** Minimum delete/search anchor length — shorter needles (e.g. ".") match inside "3.5". */
const MIN_DELETE_SEARCH_ANCHOR_LEN = 4;

type ParsedHtmlOp =
  | { kind: 'delete'; text: string; charPos: number }
  | { kind: 'replace'; deleteText: string; insertText: string; charPos: number }
  | { kind: 'insert'; text: string; charPos: number; suffixWords: string[] };

function normalizeWs(t: string): string {
  return (t || '').replace(/\s+/g, ' ').trim();
}

/** First index where two strings differ (for mismatch logs). */
function firstTextMismatchInfo(
  a: string,
  b: string,
  contextChars = 48,
): { index: number; sliceA: string; sliceB: string } | null {
  const max = Math.max(a.length, b.length);
  for (let i = 0; i < max; i++) {
    const ca = i < a.length ? a[i] : '';
    const cb = i < b.length ? b[i] : '';
    if (ca !== cb) {
      const start = Math.max(0, i - contextChars);
      return {
        index: i,
        sliceA: a.slice(start, i + contextChars),
        sliceB: b.slice(start, i + contextChars),
      };
    }
  }
  return null;
}

/** Minimum snippet length before we trust body-wide "contains" matching (avoids dollar amounts etc.). */
const MIN_SNIPPET_LEN_FOR_CONTAINS = 24;

export class RedlineInserter {
  private static escapeHtmlForWordDiff(text: string): string {
    return (text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Same shape as ProofreadWordAdapter.buildTrackChangesHtml — used when the caller's
   * originalText (e.g. stale client_request) does not match Word's live paragraph but
   * we still have the correct paragraph by index.
   */
  private static buildTrackChangesHtmlFromWordDiff(original: string, modified: string): string {
    const ops = computeWordDiff(original, modified);
    let html = '';
    for (let i = 0; i < ops.length; i++) {
      const op = ops[i];
      const chunk = RedlineInserter.escapeHtmlForWordDiff(op.words.join(' '));
      const needsTrailingSpace = i < ops.length - 1;
      const spaceSuffix = needsTrailingSpace ? ' ' : '';
      if (op.type === 'equal') {
        html += chunk + spaceSuffix;
      } else if (op.type === 'delete') {
        html += `<del class="track-change-deletion track-change-new">${chunk}</del>${spaceSuffix}`;
      } else {
        html += `<ins class="track-change-insertion track-change-new">${chunk}</ins>${spaceSuffix}`;
      }
    }
    return html;
  }

  /**
   * Inserts text as a tracked change, replacing the current selection
   * This works best on Windows Desktop where Track Changes is fully supported
   */
  static async insertAsTrackedChange(text: string): Promise<void> {
    try {
      // Ensure Track Changes is enabled
      const trackChangesEnabled = await TrackChangesManager.ensureEnabled();
      
      if (!trackChangesEnabled) {
        log('Track Changes not available, inserting as regular text');
        // Fallback to regular insertion
        await this.insertAsRegularText(text);
        return;
      }

      await Word.run(async (context) => {
        const selection = context.document.getSelection();
        
        // Load selection properties
        selection.load('text');
        await context.sync();
        
        // Only replace if there's text selected, otherwise insert at cursor
        if (selection.text && selection.text.trim().length > 0) {
          // Replace the selection with the new text
          selection.insertText(text, Word.InsertLocation.replace);
        } else {
          // If no selection, insert at cursor position
          selection.insertText(text, Word.InsertLocation.before);
        }
        
        await context.sync();
        log('Text inserted as tracked change (redline)');
      });
    } catch (error) {
      logError('Failed to insert as tracked change', error);
      // Fallback to regular insertion
      await this.insertAsRegularText(text);
    }
  }

  /**
   * Inserts text as a tracked change in a specific range
   * Note: Range objects must be obtained within the same Word.run() context.
   * This method is deprecated - use insertAsTrackedChange() instead which uses current selection.
   */
  static async insertAsTrackedChangeInRange(range: Word.Range, text: string): Promise<void> {
    try {
      const trackChangesEnabled = await TrackChangesManager.ensureEnabled();
      
      if (!trackChangesEnabled) {
        log('Track Changes not available, inserting as regular text');
        await this.insertAsRegularTextInRange(range, text);
        return;
      }

      await Word.run(async (context) => {
        // Use the range directly - it must be from the same context
        range.insertText(text, Word.InsertLocation.replace);
        await context.sync();
        log('Text inserted as tracked change in range');
      });
    } catch (error) {
      logError('Failed to insert as tracked change in range', error);
      // Fallback: use current selection instead
      await this.insertAsTrackedChange(text);
    }
  }

  /**
   * Fallback: Insert as regular text (no tracking)
   */
  private static async insertAsRegularText(text: string): Promise<void> {
    await Word.run(async (context) => {
      const selection = context.document.getSelection();
      selection.load('text');
      await context.sync();
      
      if (selection.text && selection.text.trim().length > 0) {
        selection.insertText(text, Word.InsertLocation.replace);
      } else {
        selection.insertText(text, Word.InsertLocation.before);
      }
      await context.sync();
    });
  }

  /**
   * Public fallback: replace text by finding it in the document (search or current selection).
   * Use when applyWordLevelChangesIncrementally fails (e.g. paragraph not found).
   * No track changes; replaces matching selection or first document match.
   */
  static async replaceBySearchOrSelection(
    originalText: string,
    newText: string,
    reason?: string,
  ): Promise<void> {
    log('[RedlineInserter] replaceBySearchOrSelection: start', {
      reason: reason ?? 'unspecified',
      originalLen: originalText?.length ?? 0,
      newLen: newText?.length ?? 0,
      note: 'fallback path — whole selection/body replace, not incremental track changes',
    });
    await this.insertAsRegularTextWithSearch(originalText, newText);
  }

  /**
   * Fallback: Search and replace as regular text (no tracking)
   * Optimized: First tries current selection (fast), then falls back to full document search (slow)
   */
  private static async insertAsRegularTextWithSearch(originalText: string, newText: string): Promise<void> {
    await Word.run(async (context) => {
      // OPTIMIZATION: First try current selection (fast - no search needed)
      const selection = context.document.getSelection();
      selection.load('text');
      await context.sync();
      
      const selectionText = selection.text ? selection.text.trim() : '';
      const originalTextTrimmed = originalText.trim();
      
      // Check if current selection matches the original text (normalized comparison)
      const normalizeText = (text: string) => text.replace(/\s+/g, ' ').trim();
      const selectionNormalized = normalizeText(selectionText);
      const originalNormalized = normalizeText(originalTextTrimmed);
      
      if (selectionNormalized === originalNormalized || 
          selectionText === originalTextTrimmed ||
          selectionText.includes(originalTextTrimmed) ||
          originalTextTrimmed.includes(selectionText)) {
        // Current selection matches - use it directly (FAST PATH)
        log('[RedlineInserter] replaceBySearchOrSelection: selection fast path (no tracking)', {
          selectionLen: selectionText.length,
          originalLen: originalTextTrimmed.length,
          newLen: newText.length,
        });
        
        if (selection.text && selection.text.trim().length > 0) {
          selection.insertText(newText, Word.InsertLocation.replace);
        } else {
          selection.insertText(newText, Word.InsertLocation.before);
        }
        await context.sync();
        return;
      }
      
      // FALLBACK: Selection doesn't match - search entire document (SLOW PATH)
      log('[RedlineInserter] replaceBySearchOrSelection: document search (no tracking)', {
        selectionLen: selectionText.length,
        originalLen: originalTextTrimmed.length,
      });
      const searchResults = context.document.body.search(originalTextTrimmed, {
        matchCase: false,
        matchWholeWord: false,
      });
      
      searchResults.load('items');
      await context.sync();

      if (searchResults.items.length > 0) {
        const foundRange = searchResults.items[0];
        foundRange.insertText(newText, Word.InsertLocation.replace);
        await context.sync();
      } else {
        // Last resort: use current selection even if it doesn't match
        log('[RedlineInserter] replaceBySearchOrSelection: search miss, selection fallback (no tracking)', {
          searchResultCount: searchResults.items.length,
        });
        if (selection.text && selection.text.trim().length > 0) {
          selection.insertText(newText, Word.InsertLocation.replace);
        } else {
          selection.insertText(newText, Word.InsertLocation.before);
        }
        await context.sync();
      }
    });
  }

  /**
   * Fallback: Insert as regular text in range
   * Note: Range objects must be obtained within the same Word.run() context
   */
  private static async insertAsRegularTextInRange(range: Word.Range, text: string): Promise<void> {
    await Word.run(async (context) => {
      // Use the range directly - it must be from the same context
      range.insertText(text, Word.InsertLocation.replace);
      await context.sync();
    });
  }

  /* ================================================================
   *  NEW: Incremental word-level track changes
   *
   *  1. Find the paragraph by matching its text against all paragraphs
   *  2. Select the entire paragraph
   *  3. Compute word-level diff (LCS) → group into ChangeChunks
   *  4. For each chunk (left→right):
   *       • replace / delete  → search for oldText inside the paragraph range,
   *                              disambiguate with prefix context when needed
   *       • insert            → search for the suffix word (or prefix),
   *                              insert the new words before / after it
   *     Each individual Word API operation (insertText / delete) is tracked
   *     separately by Word's Track Changes → word-level redlines.
   * ================================================================ */

  /** Derive the search anchor string used by incremental apply for a parsed op. */
  private static searchTextForOp(op: ParsedHtmlOp): string {
    if (op.kind === 'replace') return op.deleteText;
    if (op.kind === 'delete') return op.text;
    if (op.kind === 'insert' && op.suffixWords.length > 0) return op.suffixWords[0];
    return '';
  }

  private static countOccurrencesIn(fullText: string, needle: string): number {
    if (!needle) return 0;
    const hay = fullText.toLowerCase();
    const n = needle.toLowerCase();
    let count = 0;
    let pos = 0;
    while (pos < hay.length) {
      const idx = hay.indexOf(n, pos);
      if (idx === -1) break;
      count++;
      pos = idx + 1;
    }
    return count;
  }

  /** Use accepted-text bytes at charPos so spacing matches the preview baseline. */
  private static resolveDeleteSearchText(
    actualParaText: string,
    charPos: number,
    deleteText: string,
  ): string {
    if (!deleteText) return '';
    const end = Math.min(actualParaText.length, charPos + deleteText.length);
    const fromBaseline = actualParaText.slice(charPos, end);
    const slice = fromBaseline.length >= deleteText.length ? fromBaseline : deleteText;

    if (
      slice.length >= MIN_DELETE_SEARCH_ANCHOR_LEN &&
      RedlineInserter.countOccurrencesIn(actualParaText, slice) === 1
    ) {
      return slice;
    }

    const MIN = 8;
    const MAX = 120;
    let start = charPos;
    let endPos = charPos + slice.length;
    while (endPos - start < MIN && start > 0) start--;
    let candidate = actualParaText.slice(start, endPos);
    while (
      RedlineInserter.countOccurrencesIn(actualParaText, candidate) > 1 &&
      start > 0 &&
      candidate.length < MAX
    ) {
      start--;
      candidate = actualParaText.slice(start, endPos);
    }
    return candidate;
  }

  /**
   * Merge fragmented delete/replace ops from HTML (e.g. tail split into space + בנכס" + ".").
   */
  private static coalesceParsedOps(ops: ParsedHtmlOp[], actualParaText: string): ParsedHtmlOp[] {
    const sorted = [...ops].sort((a, b) => a.charPos - b.charPos);
    const out: ParsedHtmlOp[] = [];

    for (const op of sorted) {
      const prev = out[out.length - 1];
      if (!prev) {
        out.push({ ...op });
        continue;
      }

      if (
        prev.kind === 'delete' &&
        op.kind === 'delete' &&
        prev.charPos + prev.text.length === op.charPos
      ) {
        prev.text += op.text;
        continue;
      }

      if (prev.kind === 'delete' && op.kind === 'replace') {
        const prevEnd = prev.charPos + prev.text.length;
        if (op.charPos >= prev.charPos && op.charPos <= prevEnd + 2) {
          const gap =
            op.charPos > prevEnd ? actualParaText.slice(prevEnd, op.charPos) : '';
          out[out.length - 1] = {
            kind: 'replace',
            charPos: prev.charPos,
            deleteText: prev.text + gap + op.deleteText,
            insertText: op.insertText,
          };
          continue;
        }
      }

      out.push({ ...op });
    }

    return out;
  }

  /** Extract &lt;w:p&gt; from generator output (document root or bare paragraph). */
  static extractParagraphXmlFromEditedOoxml(editedOoxml: string): string | null {
    const trimmed = (editedOoxml || '').trim();
    if (!trimmed) return null;
    if (/^<w:p[\s>]/i.test(trimmed)) return trimmed;
    try {
      const doc = new DOMParser().parseFromString(trimmed, 'text/xml');
      if (doc.querySelector('parsererror')) return null;
      const p =
        doc.querySelector('p') ||
        (doc.documentElement?.localName === 'p' ? doc.documentElement : null);
      if (p) return new XMLSerializer().serializeToString(p);
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Replace the target paragraph with edited OOXML (preserves w:ins/w:del structure from preview).
   * Preferred over incremental search when full paragraph XML is available.
   */
  static async applyParagraphFromEditedOoxml(
    editedOoxml: string,
    paragraphIndex?: number,
  ): Promise<boolean> {
    const paragraphXml = RedlineInserter.extractParagraphXmlFromEditedOoxml(editedOoxml);
    if (!paragraphXml) {
      logWarn('[RedlineInserter] applyParagraphFromEditedOoxml: could not extract w:p — will use incremental');
      return false;
    }
    // Word's Range.insertOoxml rejects paragraphs that contain a <w:ins> nested directly
    // inside another <w:ins> (counterparty wrapping AI). Skip OOXML path in that case;
    // incremental search-based apply handles it correctly.
    if (RedlineInserter.hasNestedInsInsideIns(paragraphXml)) {
      log('[RedlineInserter] applyParagraphFromEditedOoxml: skipping (nested w:ins inside w:ins is not supported by insertOoxml) — will use incremental');
      return false;
    }
    const packageXml = OoxmlPackageWrapper.wrapParagraphInPackage(paragraphXml);
    if (!packageXml) {
      logWarn('[RedlineInserter] applyParagraphFromEditedOoxml: package wrap failed — will use incremental');
      return false;
    }

    try {
      const trackChangesEnabled = await TrackChangesManager.ensureEnabled();
      if (!trackChangesEnabled) {
        logError('[RedlineInserter] applyParagraphFromEditedOoxml: track changes unavailable');
        return false;
      }

      return await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let targetParagraph: any = null;
        if (
          paragraphIndex != null &&
          paragraphIndex >= 0 &&
          paragraphIndex < paragraphs.items.length
        ) {
          targetParagraph = paragraphs.items[paragraphIndex];
        } else if (paragraphs.items.length > 0) {
          targetParagraph = paragraphs.items[0];
        }

        if (!targetParagraph) {
          logError('[RedlineInserter] applyParagraphFromEditedOoxml: paragraph not found', {
            paragraphIndex: paragraphIndex ?? null,
          });
          return false;
        }

        const range = targetParagraph.getRange('Whole');
        const rangeWithOoxml = range as Word.Range & {
          insertOoxml?: (xml: string, loc: Word.InsertLocation) => void;
        };
        if (typeof rangeWithOoxml.insertOoxml !== 'function') {
          logError('[RedlineInserter] applyParagraphFromEditedOoxml: insertOoxml not supported');
          return false;
        }
        rangeWithOoxml.insertOoxml(packageXml, Word.InsertLocation.replace);
        await context.sync();
        log('[RedlineInserter] applyParagraphFromEditedOoxml: paragraph replaced via insertOoxml', {
          paragraphIndex: paragraphIndex ?? null,
          paragraphXmlLen: paragraphXml.length,
        });
        return true;
      });
    } catch (error) {
      // insertOoxml is strict about supported OOXML; fall through to incremental.
      logWarn('[RedlineInserter] applyParagraphFromEditedOoxml: insertOoxml threw — will use incremental', {
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  /**
   * Submit as-is: replace the client-request paragraph with source plain text as a single
   * tracked deletion + insertion (no word-level diff through nested counterparty markup).
   */
  static async applySubmitAsIs(
    acceptedPlainText: string,
    newPlainText: string,
    paragraphIndex?: number,
    authorName: string = 'User',
    sourceParagraphOoxml?: string | null,
  ): Promise<boolean> {
    const normAccepted = normalizeWs(acceptedPlainText);
    const normNew = normalizeWs(newPlainText);
    if (!normAccepted || !normNew) {
      logError('[RedlineInserter] applySubmitAsIs: missing accepted or new text');
      return false;
    }

    const paragraphOoxml = TrackChangesParser.createSubmitAsIsParagraphOoxml(
      normAccepted,
      normNew,
      authorName,
      sourceParagraphOoxml,
    );

    log('[RedlineInserter] applySubmitAsIs: built flat paragraph OOXML', {
      paragraphIndex: paragraphIndex ?? null,
      authorName,
      acceptedLen: normAccepted.length,
      newLen: normNew.length,
      paragraphOoxmlLen: paragraphOoxml.length,
    });

    const ooxmlOk = await RedlineInserter.applyParagraphFromEditedOoxml(
      paragraphOoxml,
      paragraphIndex,
    );
    if (ooxmlOk) {
      log('[RedlineInserter] applySubmitAsIs: applied via insertOoxml');
      await RedlineInserter.selectParagraphForSubmitAsIs(paragraphIndex, normAccepted);
      return true;
    }

    logWarn('[RedlineInserter] applySubmitAsIs: insertOoxml failed — trying whole-paragraph insertText');
    const insertTextOk = await RedlineInserter.applySubmitAsIsViaInsertText(
      normAccepted,
      normNew,
      paragraphIndex,
    );
    if (insertTextOk) {
      await RedlineInserter.selectParagraphForSubmitAsIs(paragraphIndex, normAccepted);
    }
    return insertTextOk;
  }

  /** Keep the clause paragraph selected after submit-as-is (matches incremental apply). */
  private static async selectParagraphForSubmitAsIs(
    paragraphIndex?: number,
    acceptedPlainText?: string,
  ): Promise<void> {
    try {
      await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        const items = paragraphs.items;
        for (const para of items) {
          para.load('text');
        }
        await context.sync();

        const normAccepted = acceptedPlainText ? normalizeWs(acceptedPlainText) : '';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let targetParagraph: any = null;

        if (
          paragraphIndex != null &&
          paragraphIndex >= 0 &&
          paragraphIndex < items.length
        ) {
          targetParagraph = items[paragraphIndex];
        } else if (normAccepted) {
          for (let pi = 0; pi < items.length; pi++) {
            if (normalizeWs(items[pi].text || '') === normAccepted) {
              targetParagraph = items[pi];
              break;
            }
          }
        }

        if (!targetParagraph) return;

        targetParagraph.getRange('Whole').select();
        await context.sync();
      });
    } catch (error) {
      logWarn('[RedlineInserter] selectParagraphForSubmitAsIs failed', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  /** Fallback when flat OOXML insertOoxml is unavailable. */
  private static async applySubmitAsIsViaInsertText(
    acceptedPlainText: string,
    newPlainText: string,
    paragraphIndex?: number,
  ): Promise<boolean> {
    try {
      const trackChangesEnabled = await TrackChangesManager.ensureEnabled();
      if (!trackChangesEnabled) {
        logError('[RedlineInserter] applySubmitAsIsViaInsertText: track changes unavailable');
        return false;
      }

      return await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        const items = paragraphs.items;
        for (const para of items) {
          para.load('text');
        }
        await context.sync();

        const normAccepted = normalizeWs(acceptedPlainText);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let targetParagraph: any = null;

        const exactIndices: number[] = [];
        for (let pi = 0; pi < items.length; pi++) {
          if (normalizeWs(items[pi].text || '') === normAccepted) exactIndices.push(pi);
        }

        if (exactIndices.length === 1) {
          targetParagraph = items[exactIndices[0]];
        } else if (
          paragraphIndex != null &&
          paragraphIndex >= 0 &&
          paragraphIndex < items.length
        ) {
          targetParagraph = items[paragraphIndex];
        } else if (exactIndices.length > 0) {
          targetParagraph = items[exactIndices[0]];
        }

        if (!targetParagraph) {
          logError('[RedlineInserter] applySubmitAsIsViaInsertText: paragraph not found');
          return false;
        }

        const range = targetParagraph.getRange('Whole');
        range.insertText(newPlainText, Word.InsertLocation.replace);
        await context.sync();
        log('[RedlineInserter] applySubmitAsIsViaInsertText: replaced paragraph via insertText');
        return true;
      });
    } catch (error) {
      logError('[RedlineInserter] applySubmitAsIsViaInsertText failed', error);
      return false;
    }
  }

  /** Detect <w:ins ...><w:ins ...>...</w:ins></w:ins> nesting (Word rejects this in insertOoxml). */
  private static hasNestedInsInsideIns(paragraphXml: string): boolean {
    try {
      const xmlDoc = new DOMParser().parseFromString(paragraphXml, 'text/xml');
      if (xmlDoc.querySelector('parsererror')) return false;
      const inspect = (el: Element, insideIns: boolean): boolean => {
        const local = el.localName || el.tagName.split(':').pop() || '';
        const isIns = local === 'ins';
        if (isIns && insideIns) return true;
        const childInside = insideIns || isIns;
        for (const child of Array.from(el.childNodes)) {
          if (child.nodeType === Node.ELEMENT_NODE && inspect(child as Element, childInside)) {
            return true;
          }
        }
        return false;
      };
      return inspect(xmlDoc.documentElement, false);
    } catch {
      return false;
    }
  }

  /**
   * Structured summary of parsed track-changes HTML — for submit / incremental diagnostics.
   * Exposed so CrossCheckClauseCard can log the same shape before calling RedlineInserter.
   */
  static summarizeTrackChangesHtmlOps(html: string): {
    opCount: number;
    anyOpExceedsWordJsSearchLimit: boolean;
    anyInsertMissingSearchAnchor: boolean;
    trailingInsertOnly: boolean;
    maxCharPos: number;
    ops: Array<{
      kind: string;
      charPos: number;
      searchTextLen: number;
      hasSearchAnchor: boolean;
      suffixWords: string[];
      insertTextLen?: number;
      deleteTextLen?: number;
      insertSnippet?: string;
      failureHint?: string;
    }>;
  } {
    const snippet = (s: string, max = 80) => {
      const compact = (s || '').replace(/\s+/g, ' ').trim();
      return compact.length > max ? `${compact.slice(0, max)}...` : compact;
    };
    const ops = RedlineInserter.parseHtmlOps(html);
    let maxSearchTextLen = 0;
    let maxCharPos = 0;
    let anyInsertMissingSearchAnchor = false;
    const summarized = ops.map((op) => {
      const searchText = RedlineInserter.searchTextForOp(op);
      maxSearchTextLen = Math.max(maxSearchTextLen, searchText.length);
      maxCharPos = Math.max(maxCharPos, op.charPos);
      let failureHint: string | undefined;
      if (op.kind === 'insert' && searchText.length === 0) {
        anyInsertMissingSearchAnchor = true;
        failureHint =
          op.suffixWords.length === 0
            ? 'trailing_insert_no_suffix_anchor'
            : 'insert_empty_search_anchor';
      }
      return {
        kind: op.kind,
        charPos: op.charPos,
        searchTextLen: searchText.length,
        hasSearchAnchor: searchText.length > 0,
        suffixWords: op.kind === 'insert' ? op.suffixWords : [],
        ...(op.kind === 'insert'
          ? { insertTextLen: op.text.length, insertSnippet: snippet(op.text) }
          : {}),
        ...(op.kind === 'replace'
          ? { deleteTextLen: op.deleteText.length, insertTextLen: op.insertText.length }
          : {}),
        ...(op.kind === 'delete' ? { deleteTextLen: op.text.length } : {}),
        failureHint,
      };
    });
    const trailingInsertOnly =
      ops.length === 1 &&
      ops[0].kind === 'insert' &&
      !RedlineInserter.searchTextForOp(ops[0]);
    return {
      opCount: ops.length,
      anyOpExceedsWordJsSearchLimit: maxSearchTextLen > WORD_JS_MAX_SEARCH_STRING_LENGTH,
      anyInsertMissingSearchAnchor,
      trailingInsertOnly,
      maxCharPos,
      ops: summarized,
    };
  }

  /** Whether parsed HTML ops can all use Range.search (Word.js 255-char limit). */
  private static incrementalOpsSearchLens(html: string): {
    opCount: number;
    anyOpExceedsWordJsSearchLimit: boolean;
  } {
    const summary = RedlineInserter.summarizeTrackChangesHtmlOps(html);
    return {
      opCount: summary.opCount,
      anyOpExceedsWordJsSearchLimit: summary.anyOpExceedsWordJsSearchLimit,
    };
  }

  /**
   * Incremental word-level track changes using SEARCH-BASED positioning.
   *
   * Why search-based: When Track Changes is ON, each edit creates tracked deletion
   * ranges + tracked insertion ranges. getTextRanges returns ALL of these, making
   * index-based delta tracking impossible. Search bypasses this entirely by finding
   * the target text by content, not by position.
   *
   * 1. Find paragraph by index (or text fallback)
   * 2. Accept existing track changes (clean slate)
   * 3. Compute word-level diff → group into ChangeChunks with prefix/suffix context
   * 4. Apply each chunk LEFT-TO-RIGHT using Range.search() to find target text
   * 5. Each operation is a single Word API call → word-level track changes
   */
  static async applyWordLevelChangesIncrementally(
    originalText: string,
    newText: string,
    trackChangesHtml: string,
    paragraphIndex?: number,
  ): Promise<boolean> {
    try {
      const snippet = (s: string, max = 140) => {
        const compact = (s || '').replace(/\s+/g, ' ').trim();
        return compact.length > max ? `${compact.slice(0, max)}...` : compact;
      };
      log('[RedlineInserter] incremental apply: start', {
        paragraphIndexHint: paragraphIndex ?? null,
        originalTextLen: originalText?.length ?? 0,
        newTextLen: newText?.length ?? 0,
        trackChangesHtmlLen: trackChangesHtml?.length ?? 0,
        originalSnippet: snippet(originalText),
        newSnippet: snippet(newText),
      });

      // Ensure Track Changes is ON
      const trackChangesEnabled = await TrackChangesManager.ensureEnabled();
      if (!trackChangesEnabled) {
        log('[RedlineInserter] incremental apply: track changes unavailable, using regular replace');
        await this.insertAsRegularTextWithSearch(originalText, newText);
        return true;
      }


      // --- apply inside Word.run ---
      // We first read the ACTUAL paragraph text from Word, then compute the diff.
      // This avoids mismatches from XML extraction vs. actual document text.

      const incrementalOk = await Word.run(async (context) => {
        // 1. Resolve paragraph (prefer body text over stale agent index — e.g. after apply_word_structure)
        const paragraphs = context.document.body.paragraphs;

        paragraphs.load('items');
        await context.sync();

        for (const para of paragraphs.items) {
          para.load('text');
        }
        await context.sync();

        const items = paragraphs.items;
        const normOrig = normalizeWs(originalText);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let targetParagraph: any = null;
        let resolvedWordIndex = -1;
        let resolvedBy:
          | 'exactUnique'
          | 'exactAmbiguousUsedHint'
          | 'exactAmbiguousFirst'
          | 'containsUsedHint'
          | 'containsUnique'
          | 'containsShortestOfMany'
          | 'prefixMatch'
          | 'indexFallback' = 'indexFallback';

        const exactIndices: number[] = [];
        for (let pi = 0; pi < items.length; pi++) {
          if (normalizeWs(items[pi].text || '') === normOrig) exactIndices.push(pi);
        }

        if (exactIndices.length === 1) {
          resolvedWordIndex = exactIndices[0];
          targetParagraph = items[resolvedWordIndex];
          resolvedBy = 'exactUnique';
        } else if (exactIndices.length > 1) {
          if (paragraphIndex != null && exactIndices.includes(paragraphIndex)) {
            resolvedWordIndex = paragraphIndex;
            targetParagraph = items[paragraphIndex];
            resolvedBy = 'exactAmbiguousUsedHint';
          } else {
            resolvedWordIndex = exactIndices[0];
            targetParagraph = items[exactIndices[0]];
            resolvedBy = 'exactAmbiguousFirst';
          }
        } else {
          const contains: number[] = [];
          if (normOrig.length >= MIN_SNIPPET_LEN_FOR_CONTAINS) {
            for (let pi = 0; pi < items.length; pi++) {
              if (normalizeWs(items[pi].text || '').includes(normOrig)) contains.push(pi);
            }
          }
          if (
            paragraphIndex != null &&
            paragraphIndex >= 0 &&
            paragraphIndex < items.length &&
            contains.includes(paragraphIndex)
          ) {
            resolvedWordIndex = paragraphIndex;
            targetParagraph = items[paragraphIndex];
            resolvedBy = 'containsUsedHint';
          } else if (contains.length === 1) {
            resolvedWordIndex = contains[0];
            targetParagraph = items[contains[0]];
            resolvedBy = 'containsUnique';
          } else if (contains.length > 1) {
            const sorted = [...contains].sort(
              (a, b) =>
                normalizeWs(items[a].text || '').length - normalizeWs(items[b].text || '').length,
            );
            resolvedWordIndex = sorted[0];
            targetParagraph = items[sorted[0]];
            resolvedBy = 'containsShortestOfMany';
          } else {
            // Prefix fallback: find a paragraph that STARTS WITH the first 120 chars of originalText.
            // This handles the case where a previous issue in the same apply round already modified
            // the paragraph body (so the full original no longer appears verbatim), but the opening
            // of the paragraph hasn't changed.
            const PREFIX_LEN = 120;
            const normPrefix = normOrig.slice(0, PREFIX_LEN);
            if (normPrefix.length >= MIN_SNIPPET_LEN_FOR_CONTAINS) {
              const prefixMatches: number[] = [];
              for (let pi = 0; pi < items.length; pi++) {
                if (normalizeWs(items[pi].text || '').startsWith(normPrefix)) prefixMatches.push(pi);
              }
              if (prefixMatches.length === 1) {
                resolvedWordIndex = prefixMatches[0];
                targetParagraph = items[prefixMatches[0]];
                resolvedBy = 'prefixMatch';
              } else if (prefixMatches.length > 1 && paragraphIndex != null && prefixMatches.includes(paragraphIndex)) {
                resolvedWordIndex = paragraphIndex;
                targetParagraph = items[paragraphIndex];
                resolvedBy = 'prefixMatch';
              } else if (prefixMatches.length > 1) {
                logError('[RedlineInserter] incremental apply: ambiguous prefix match');
                return false;
              }
            }

            if (!targetParagraph && paragraphIndex != null && paragraphIndex >= 0 && paragraphIndex < items.length) {
              resolvedWordIndex = paragraphIndex;
              targetParagraph = items[paragraphIndex];
              resolvedBy = 'indexFallback';
            }
          }
        }

        if (!targetParagraph) {
          logError('[RedlineInserter] incremental apply: paragraph not resolved', {
            paragraphIndexHint: paragraphIndex ?? null,
            paragraphCount: items.length,
            originalSnippet: snippet(originalText),
          });
          throw new Error('Could not find paragraph. Please select it and try again.');
        }

        const apiPlainText = (targetParagraph.text || '').trim();
        const actualParaTextFromApi = normalizeWs(apiPlainText);
        const actualParaTextFromApiStripped = normalizeWs(
          TrackChangesParser.stripRefFieldNoise(apiPlainText),
        );

        let acceptedRawFromParagraphOoxml: string | null = null;
        let ooxmlPackageLen = 0;
        let ooxmlHasFieldMarkers = false;
        let ooxmlParagraphXmlLen = 0;
        let ooxmlExtractError: string | null = null;
        try {
          const ooxmlResult = (targetParagraph as any).getOoxml();
          await context.sync();
          const pkg = ooxmlResult?.value;
          if (typeof pkg === 'string' && pkg.length > 0) {
            ooxmlPackageLen = pkg.length;
            ooxmlHasFieldMarkers =
              /<w:fldChar\b/i.test(pkg) ||
              /<w:instrText\b/i.test(pkg) ||
              /\bREF\s+/i.test(pkg);
            const pXml = DocumentReader.paragraphOrBodyXmlFromOoxmlPackage(pkg);
            ooxmlParagraphXmlLen = pXml?.length ?? 0;
            if (pXml?.trim()) {
              acceptedRawFromParagraphOoxml = TrackChangesParser.extractAcceptedText(pXml);
            }
          }
        } catch (e) {
          ooxmlExtractError = e instanceof Error ? e.message : String(e);
        }

        const actualParaTextFromOoxml = acceptedRawFromParagraphOoxml
          ? normalizeWs(acceptedRawFromParagraphOoxml)
          : '';

        // Use OOXML-derived accepted text for positioning when available: it matches
        // TrackChangesParser semantics used for client_request / AI XML. paragraph.text
        // can diverge when REF / cross-reference fields resolve to different surface text.
        const actualParaText =
          actualParaTextFromOoxml.length > 0 ? actualParaTextFromOoxml : actualParaTextFromApi;

        const passesIndexGuardApi =
          actualParaTextFromApi.includes(normOrig) || actualParaTextFromApiStripped.includes(normOrig);
        const passesIndexGuardOoxml =
          actualParaTextFromOoxml.length > 0 && actualParaTextFromOoxml.includes(normOrig);

        log('[RedlineInserter] incremental apply: paragraph resolved', {
          resolvedWordIndex,
          resolvedBy,
          paragraphIndexHint: paragraphIndex ?? null,
          paragraphCount: items.length,
          originalNormLen: normOrig.length,
          apiTextLen: actualParaTextFromApi.length,
          apiStrippedLen: actualParaTextFromApiStripped.length,
          ooxmlAcceptedLen: actualParaTextFromOoxml.length,
          usedOoxmlForOps: actualParaTextFromOoxml.length > 0,
          ooxmlPackageLen,
          ooxmlParagraphXmlLen,
          ooxmlHasFieldMarkers,
          ooxmlExtractError,
          passesIndexGuardApi,
          passesIndexGuardOoxml,
          actualParaLenForOps: actualParaText.length,
          actualSnippet: snippet(actualParaText),
        });

        let effectiveTrackChangesHtml = trackChangesHtml;
        let usedLiveParagraphWordDiff = false;

        if (
          resolvedBy === 'indexFallback' &&
          !passesIndexGuardApi &&
          !passesIndexGuardOoxml
        ) {
          const mismatchApi = firstTextMismatchInfo(normOrig, actualParaTextFromApi);
          const mismatchOoxml =
            actualParaTextFromOoxml.length > 0
              ? firstTextMismatchInfo(normOrig, actualParaTextFromOoxml)
              : null;
          log(
            '[RedlineInserter] incremental apply: index guard failed (stored original ≠ live paragraph). Typical causes: extra text in Word (e.g. prior edits), REF fields, or stale client_request. Attempting live word-diff recovery.',
            {
              paragraphIndexHint: paragraphIndex ?? null,
              resolvedWordIndex,
              originalNormLen: normOrig.length,
              apiTextLen: actualParaTextFromApi.length,
              ooxmlAcceptedLen: actualParaTextFromOoxml.length,
              ooxmlHasFieldMarkers,
              mismatchVsApiText: mismatchApi,
              mismatchVsOoxmlAccepted: mismatchOoxml,
              originalSnippet: snippet(normOrig),
              apiSnippet: snippet(actualParaTextFromApi),
            },
          );

          const liveBaseline = actualParaText;
          const newNorm = normalizeWs(newText);
          if (!liveBaseline || !newNorm) {
            logError('[RedlineInserter] incremental apply: cannot recover — empty live or target text', {
              resolvedWordIndex,
            });
            return false;
          }
          if (liveBaseline === newNorm) {
            log('[RedlineInserter] incremental apply: live paragraph already matches target — nothing to apply');
            return true;
          }
          effectiveTrackChangesHtml = RedlineInserter.buildTrackChangesHtmlFromWordDiff(
            liveBaseline,
            newNorm,
          );
          usedLiveParagraphWordDiff = true;
          log('[RedlineInserter] incremental apply: using synthetic trackChangesHtml from live paragraph vs target (word diff)', {
            liveBaselineLen: liveBaseline.length,
            newNormLen: newNorm.length,
            syntheticHtmlLen: effectiveTrackChangesHtml.length,
          });
        }

        const opsSummary = RedlineInserter.summarizeTrackChangesHtmlOps(effectiveTrackChangesHtml);
        const incLens = RedlineInserter.incrementalOpsSearchLens(effectiveTrackChangesHtml);
        const canUseIncremental =
          incLens.opCount > 0 && !incLens.anyOpExceedsWordJsSearchLimit;
        log('[RedlineInserter] incremental apply: parsed ops summary', {
          usedLiveParagraphWordDiff,
          effectiveTrackChangesHtmlLen: effectiveTrackChangesHtml.length,
          actualParaLenForOps: actualParaText.length,
          insertAtParagraphEnd:
            opsSummary.trailingInsertOnly &&
            opsSummary.maxCharPos >= actualParaText.length - 1,
          ...opsSummary,
        });
        log('[RedlineInserter] incremental apply: mode decision', {
          resolvedBy,
          usedLiveParagraphWordDiff,
          opCount: incLens.opCount,
          anyOpExceedsWordJsSearchLimit: incLens.anyOpExceedsWordJsSearchLimit,
          anyInsertMissingSearchAnchor: opsSummary.anyInsertMissingSearchAnchor,
          trailingInsertOnly: opsSummary.trailingInsertOnly,
          canUseIncremental,
        });

        if (!canUseIncremental) {
          logError('[RedlineInserter] incremental apply: cannot run — no ops or search string too long', {
            opCount: incLens.opCount,
            anyOpExceedsWordJsSearchLimit: incLens.anyOpExceedsWordJsSearchLimit,
          });
          return false;
        }

        const rawOps = RedlineInserter.parseHtmlOps(effectiveTrackChangesHtml);
        const coalescedOps = RedlineInserter.coalesceParsedOps(rawOps, actualParaText);

        if (coalescedOps.length === 0) {
          log('[RedlineInserter] incremental apply: no parsed ops — nothing to apply');
          return true;
        }

        log('[RedlineInserter] incremental apply: coalesced ops', {
          rawOpCount: rawOps.length,
          coalescedOpCount: coalescedOps.length,
          coalesced: coalescedOps.map((op) => ({
            kind: op.kind,
            charPos: op.charPos,
            ...(op.kind === 'replace'
              ? {
                  deleteTextLen: op.deleteText.length,
                  insertTextLen: op.insertText.length,
                  deleteSnippet: snippet(op.deleteText),
                }
              : {}),
            ...(op.kind === 'delete' ? { deleteTextLen: op.text.length } : {}),
          })),
        });

        targetParagraph.getRange('Whole').select();
        await context.sync();

        // Right-to-left so earlier charPos values stay valid against the original baseline text.
        const pass1 = coalescedOps.sort((a, b) => b.charPos - a.charPos);
        let failed = 0;

        for (let opIndex = 0; opIndex < pass1.length; opIndex++) {
          const op = pass1[opIndex];
          let searchText = '';
          if (op.kind === 'replace') {
            // Replace must search only the deleted span (never a prefix-extended anchor).
            searchText =
              actualParaText.slice(op.charPos, op.charPos + op.deleteText.length) || op.deleteText;
          } else if (op.kind === 'delete') {
            searchText = RedlineInserter.resolveDeleteSearchText(
              actualParaText,
              op.charPos,
              op.text,
            );
          } else {
            searchText = RedlineInserter.searchTextForOp(op);
          }

          log('[RedlineInserter] incremental apply: op attempt', {
            opIndex,
            totalOps: pass1.length,
            applyOrder: 'right_to_left',
            kind: op.kind,
            charPos: op.charPos,
            searchTextLen: searchText.length,
            searchSnippet: snippet(searchText),
            ambiguousShortAnchor:
              op.kind !== 'insert' &&
              searchText.length > 0 &&
              searchText.length < MIN_DELETE_SEARCH_ANCHOR_LEN,
            ...(op.kind === 'insert'
              ? {
                  insertTextLen: op.text.length,
                  insertSnippet: snippet(op.text),
                  suffixWords: op.suffixWords,
                  atParagraphEnd: op.charPos >= actualParaText.length - 1,
                }
              : {}),
            ...(op.kind === 'replace'
              ? { deleteTextLen: op.deleteText.length, insertTextLen: op.insertText.length }
              : {}),
          });

          if (searchText === '') {
            // Trailing-insert case: an `insert` op at or past the paragraph end has no
            // following text to anchor on. Append directly to the paragraph's end range
            // — Word will record this as a tracked insertion when Track Changes is on.
            const isTrailingInsert =
              op.kind === 'insert' &&
              op.suffixWords.length === 0 &&
              op.charPos >= actualParaText.length - 1;

            if (isTrailingInsert) {
              try {
                const insertText = op.text.trim();
                if (!insertText) {
                  log('[RedlineInserter] incremental apply: trailing insert is empty after trim, skipping', {
                    opIndex,
                    charPos: op.charPos,
                  });
                  continue;
                }
                const endRange = targetParagraph.getRange('End');
                endRange.insertText(' ' + insertText, Word.InsertLocation.after);
                await context.sync();
                log('[RedlineInserter] incremental apply: trailing insert appended at paragraph end', {
                  opIndex,
                  charPos: op.charPos,
                  insertLen: insertText.length,
                  insertSnippet: snippet(insertText),
                });
                log('[TrackChangeAuthor] Word trailing insert via insertText', {
                  opIndex,
                  note: 'Word attributes insertText to the current Word user, not w:author from preview OOXML',
                  insertSnippet: snippet(insertText),
                });
                continue;
              } catch (appendErr) {
                failed++;
                logError('[RedlineInserter] incremental apply: trailing insert append failed', {
                  opIndex,
                  charPos: op.charPos,
                  insertSnippet: snippet(op.text),
                  error: appendErr,
                });
                continue;
              }
            }

            failed++;
            logError('[RedlineInserter] incremental apply: op skipped — no search anchor', {
              opIndex,
              kind: op.kind,
              charPos: op.charPos,
              actualParaLen: actualParaText.length,
              atParagraphEnd: op.charPos >= actualParaText.length - 1,
              ...(op.kind === 'insert'
                ? {
                    suffixWords: op.suffixWords,
                    insertSnippet: snippet(op.text),
                    likelyCause:
                      op.suffixWords.length === 0
                        ? 'trailing_insert_no_following_text_for_suffix_anchor'
                        : 'insert_with_empty_suffix_word',
                  }
                : {}),
            });
            continue;
          }

          const searchTooLong = searchText.length > WORD_JS_MAX_SEARCH_STRING_LENGTH;
          const occIdx = RedlineInserter.countOccurrencesBefore(actualParaText, searchText, op.charPos);

          if (searchTooLong) {
            logError('[RedlineInserter] incremental apply: search text too long', {
              kind: op.kind,
              charPos: op.charPos,
              searchTextLen: searchText.length,
              wordLimit: WORD_JS_MAX_SEARCH_STRING_LENGTH,
              searchSnippet: snippet(searchText),
            });
            failed++;
            continue;
          }

          const freshRange = targetParagraph.getRange('Whole');
          const results = (freshRange as any).search(searchText, { matchCase: false });
          results.load('items');
          try {
            await context.sync();
            const resultCount = results.items?.length ?? 0;
            const target =
              resultCount > 0 ? results.items[occIdx] ?? results.items[resultCount - 1] : null;

            if (!target) {
              logError('[RedlineInserter] incremental apply: search target not found', {
                kind: op.kind,
                charPos: op.charPos,
                occIdx,
                resultCount,
                searchSnippet: snippet(searchText),
              });
              failed++;
              continue;
            }

            if (op.kind === 'replace') {
              // To produce OOXML order <w:del>old</w:del><w:ins>new</w:ins> (matching the
              // preview), insert AFTER the original range first, then delete it. Using
              // InsertLocation.replace would emit <w:ins>new</w:ins><w:del>old</w:del>
              // (insertion shown before deletion in Word), which mismatches the card preview.
              const leading = op.deleteText.match(/^\s+/)?.[0] || '';
              const trailing = op.deleteText.match(/\s+$/)?.[0] || '';
              const insertCore = op.insertText.replace(/\s+/g, ' ').trim();
              const insertWithBoundary = `${leading}${insertCore}${trailing}`;
              log('[RedlineInserter] incremental apply: replace via insert-after + delete', {
                charPos: op.charPos,
                deleteLen: op.deleteText.length,
                insertLen: insertWithBoundary.length,
                leadingSpace: leading.length,
                trailingSpace: trailing.length,
                insertSnippet: snippet(insertWithBoundary),
              });
              target.insertText(insertWithBoundary, Word.InsertLocation.after);
              await context.sync();
              (target as any).delete();
            } else if (op.kind === 'delete') {
              (target as any).delete();
            } else {
              target.insertText(op.text.trim() + ' ', Word.InsertLocation.before);
            }

            await context.sync();
          } catch (opErr) {
            failed++;
            logError('[RedlineInserter] incremental apply: op failed', {
              kind: op.kind,
              charPos: op.charPos,
              searchSnippet: snippet(searchText),
              error: opErr,
            });
          }
        }

        targetParagraph.getRange('Whole').select();
        await context.sync();

        log('[RedlineInserter] incremental apply: finished', {
          totalOps: pass1.length,
          failedOps: failed,
          ok: failed === 0,
          resolvedBy,
          resolvedWordIndex,
        });
        return failed === 0;
      });

      log('[RedlineInserter] incremental apply: returned', { incrementalOk });
      return incrementalOk;

    } catch (error) {
      // Top-level error handler - catches any errors not caught in individual operations
      logError('[RedlineInserter] incremental apply failed', error);
      throw error; // Re-throw to let caller handle
    }
  }

  /* ------ search helper (called inside Word.run) ------ */

  /**
   * Search for `searchText` inside `paraRange`.
   * If multiple matches, disambiguate using prefix and suffix context.
   * Returns the first (leftmost) correct match, or null.
   *
   * Disambiguation uses the paragraph’s actual text: for each candidate we read
   * the text before and after from the document and check word-by-word (any whitespace),
   * so spacing is derived from the document, not assumed.
   */

  /**
   * Parses trackChangesHtml into a list of change operations in document order.
   *
   * Each op carries a charPos (character offset in the paragraph's accepted text) that lets
   * countOccurrencesBefore pick the exact search result without prefix/suffix word matching.
   *
   * Consecutive new-del + new-ins segments with no intervening text are merged into a single
   * replace op, so Word records both the deletion and insertion in one operation.
   *
   * Accepted text = regular text + old insertions + new-deletion text.
   * Old deletions (track-change-deletion WITHOUT track-change-new) are excluded because they
   * are already tracked as deleted in Word and invisible to Range.search().
   */
  private static parseHtmlOps(html: string): ParsedHtmlOp[] {
    const body = new DOMParser().parseFromString(html, 'text/html').body;

    type Seg = { kind: 'text'; text: string } | { kind: 'new-del'; text: string } | { kind: 'new-ins'; text: string };

    const isNewDel = (el: Element) =>
      el.tagName === 'DEL' && el.classList.contains('track-change-deletion') && el.classList.contains('track-change-new');
    const isNewIns = (el: Element) =>
      el.tagName === 'INS' && el.classList.contains('track-change-insertion') && el.classList.contains('track-change-new');
    const isOldDel = (el: Element) =>
      el.tagName === 'DEL' && el.classList.contains('track-change-deletion') && !el.classList.contains('track-change-new');

    function collectText(node: Node): string {
      if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
      if (node.nodeType === Node.ELEMENT_NODE) {
        if (isOldDel(node as Element)) return '';
        return Array.from(node.childNodes).map(collectText).join('');
      }
      return '';
    }

    // Build flat segment list; whitespace-only text nodes are omitted.
    const segs: Seg[] = [];
    for (const node of Array.from(body.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        const t = node.textContent || '';
        if (t.trim()) segs.push({ kind: 'text', text: t });
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        if (isNewDel(el)) {
          segs.push({ kind: 'new-del', text: el.textContent || '' });
        } else if (isNewIns(el)) {
          segs.push({ kind: 'new-ins', text: el.textContent || '' });
        } else if (!isOldDel(el)) {
          const t = collectText(el);
          if (t.trim()) segs.push({ kind: 'text', text: t });
        }
        // else: old-del → skip
      }
    }

    const ops: ParsedHtmlOp[] = [];
    let charPos = 0;
    let i = 0;

    while (i < segs.length) {
      const seg = segs[i];
      if (seg.kind === 'text') {
        charPos += seg.text.length;
        i++;
      } else if (seg.kind === 'new-del') {
        const pos = charPos;
        charPos += seg.text.length;
        // Pair with an immediately following new-ins → single replace op.
        if (i + 1 < segs.length && segs[i + 1].kind === 'new-ins') {
          const insText = (segs[i + 1] as { kind: 'new-ins'; text: string }).text;
          ops.push({ kind: 'replace', deleteText: seg.text, insertText: insText, charPos: pos });
          i += 2;
        } else {
          ops.push({ kind: 'delete', text: seg.text, charPos: pos });
          i++;
        }
      } else {
        // new-ins: suffix comes only from subsequent text segments (stable after deletions).
        const pos = charPos;
        const suffixWords: string[] = [];
        for (let j = i + 1; j < segs.length && suffixWords.length < 3; j++) {
          if (segs[j].kind === 'text') {
            suffixWords.push(...(segs[j] as { kind: 'text'; text: string }).text.trim().split(/\s+/).filter(Boolean));
          }
        }
        ops.push({ kind: 'insert', text: seg.text, charPos: pos, suffixWords: suffixWords.slice(0, 3) });
        i++;
      }
    }

    return ops;
  }

  /**
   * Counts non-overlapping occurrences of searchText in fullText before charPos.
   * The count is used as a 0-based index into Range.search() results to select
   * the correct occurrence without relying on prefix/suffix word matching.
   */
  private static countOccurrencesBefore(fullText: string, searchText: string, charPos: number): number {
    const segment = fullText.substring(0, charPos).toLowerCase();
    const needle = searchText.toLowerCase();
    let count = 0;
    let pos = 0;
    while (pos < segment.length) {
      const idx = segment.indexOf(needle, pos);
      if (idx === -1) break;
      count++;
      pos = idx + 1;
    }
    return count;
  }

  /**
   * Revert paragraph by rejecting track changes in a time window only. No fallback replace.
   *
   * @param paragraphIndex - 0-based paragraph index in the Word document
   * @param submittedAt - epoch ms when the submit was performed (required; no revert if null)
   * @param beforeText - unused; kept for API compatibility / logging
   * @param windowBefore - ms before submittedAt to include (default 60 000)
   * @param windowAfter - ms after submittedAt to include (default 60 000)
   * @returns true if reverted (rejected at least one track change), false if nothing found
   */
  static async revertTrackedChangesInParagraph(
    paragraphIndex: number,
    submittedAt: number | null | undefined,
    beforeText: string,
    windowBefore = 60_000,
    windowAfter = 60_000,
  ): Promise<boolean> {
    const snippet = (s: string, max = 80) =>
      (s ?? '').replace(/\s+/g, ' ').trim().slice(0, max) + (s && s.length > max ? '…' : '');

    try {
      return await Word.run(async (context) => {
        log('[RedlineInserter] revert: start', {
          paragraphIndex,
          submittedAt: submittedAt ?? null,
          beforeTextLen: beforeText?.length ?? 0,
          beforeSnippet: snippet(beforeText),
        });

        if (submittedAt == null) {
          log('[RedlineInserter] revert: no submittedAt – cannot revert (no fallback)');
          return false;
        }

        const body = context.document.body;
        body.load('paragraphs');
        await context.sync();

        const paragraphs = body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        const totalParagraphs = paragraphs.items.length;
        if (paragraphIndex < 0 || paragraphIndex >= totalParagraphs) {
          logError('[RedlineInserter] revert: paragraph index out of range', {
            paragraphIndex,
            totalParagraphs,
          });
          return false;
        }

        const targetParagraph = paragraphs.items[paragraphIndex];
        const lowerBound = new Date(submittedAt - windowBefore);
        const upperBound = new Date(submittedAt + windowAfter);
        log('[RedlineInserter] revert: time window', {
          submittedAtDate: new Date(submittedAt).toISOString(),
          lowerBound: lowerBound.toISOString(),
          upperBound: upperBound.toISOString(),
        });

        const trackedChanges = (targetParagraph as any).getTrackedChanges();
        trackedChanges.load('items');
        await context.sync();

        const items = trackedChanges.items || [];
        log('[RedlineInserter] revert: tracked changes in paragraph', { count: items.length });

        if (items.length === 0) {
          log('[RedlineInserter] revert: no tracked changes in paragraph – nothing to revert');
          return false;
        }

        for (const tc of items) {
          tc.load(['date', 'type']);
        }
        await context.sync();

        const toReject: any[] = [];
        for (let i = 0; i < items.length; i++) {
          const tc = items[i];
          const tcDate = tc.date ? new Date(tc.date) : null;
          const inWindow = tcDate && tcDate >= lowerBound && tcDate <= upperBound;
          log('[RedlineInserter] revert: tc', i, {
            date: tcDate?.toISOString?.() ?? null,
            inWindow,
          });
          if (inWindow) toReject.push(tc);
        }

        if (toReject.length === 0) {
          log('[RedlineInserter] revert: no tracked changes in time window – not reverting (no fallback)');
          return false;
        }

        log('[RedlineInserter] revert: rejecting', toReject.length, 'tracked changes in time window');
        for (const tc of toReject) {
          tc.reject();
        }
        await context.sync();
        log('[RedlineInserter] revert: done (via reject)');
        return true;
      });
    } catch (error) {
      logError('[RedlineInserter] revert tracked changes failed', error);
      throw error;
    }
  }

  /**
   * Accept all tracked changes in a paragraph (Word native accept).
   * Returns refreshed paragraph OOXML when available.
   */
  static async acceptTrackedChangesInParagraph(
    paragraphIndex: number,
  ): Promise<{ acceptedCount: number; paragraphOoxml: string | null }> {
    try {
      return await Word.run(async (context) => {
        const body = context.document.body;
        body.load('paragraphs');
        await context.sync();

        const paragraphs = body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        const totalParagraphs = paragraphs.items.length;
        if (paragraphIndex < 0 || paragraphIndex >= totalParagraphs) {
          logError('[RedlineInserter] accept: paragraph index out of range', {
            paragraphIndex,
            totalParagraphs,
          });
          return { acceptedCount: 0, paragraphOoxml: null };
        }

        const targetParagraph = paragraphs.items[paragraphIndex];
        const trackedChanges = (targetParagraph as any).getTrackedChanges();
        trackedChanges.load('items');
        await context.sync();

        const items = trackedChanges.items || [];
        if (items.length === 0) {
          log('[RedlineInserter] accept: no tracked changes in paragraph');
          const ooxmlOnly = await RedlineInserter.readParagraphOoxml(targetParagraph, context);
          return { acceptedCount: 0, paragraphOoxml: ooxmlOnly };
        }

        for (const tc of items) {
          tc.accept();
        }
        await context.sync();
        log('[RedlineInserter] accept: accepted tracked changes', { count: items.length });

        const paragraphOoxml = await RedlineInserter.readParagraphOoxml(targetParagraph, context);
        return { acceptedCount: items.length, paragraphOoxml };
      });
    } catch (error) {
      logError('[RedlineInserter] accept tracked changes failed', error);
      throw error;
    }
  }

  private static async readParagraphOoxml(
    targetParagraph: any,
    context: Word.RequestContext,
  ): Promise<string | null> {
    try {
      const ooxmlResult = (targetParagraph as any).getOoxml();
      await context.sync();
      const pkg = ooxmlResult?.value;
      if (typeof pkg !== 'string' || !pkg.length) return null;
      return DocumentReader.paragraphOrBodyXmlFromOoxmlPackage(pkg) || null;
    } catch (e) {
      logError('[RedlineInserter] accept: failed to read paragraph OOXML', e);
      return null;
    }
  }
}
